import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useGetLocalesQuery } from '../../redux/services/api';
import {
  addFormField,
  type FormBuilderField,
  selectGroupById,
  setSelectedGroupId,
} from '../../redux/slices/form-builder-slice';
import FormBuilderFieldPreview from './dnd/form-builder-field-preview.component';
import FormBuilderDynamicField from './fields/form-builder-dynamic-field.component';
import FormBuilderAccordion from './form-builder-accordion.component';
import FormBuilderAddButton from './form-builder-add-button.component';
import {
  InlineAddFieldPlaceholder,
  type InlineAddFieldPlaceholderCommitPayload,
} from './inline-add-field-placeholder.component';
import { createTranslations } from './form-builder-utils';

interface FormBuilderGroupProps {
  groupId: number | string;
  className?: string;
}

const FormBuilderGroup = ({ groupId, className }: FormBuilderGroupProps) => {
  const [isAddingField, setIsAddingField] = useState(false);

  const group = useAppSelector((state) => selectGroupById(state, groupId));
  const groupCaption = group?.caption?.translations?.[0]?.value ?? '';
  const groupFields = useMemo(() => group?.fields ?? [], [group?.fields]);

  const dispatch = useAppDispatch();
  const { data: locales } = useGetLocalesQuery();

  const onGroupClick = useCallback(() => {
    dispatch(setSelectedGroupId(groupId));
  }, [groupId, dispatch]);

  const currentGroupFields = useMemo(() => {
    return groupFields.filter((field) => !field.removed);
  }, [groupFields]);

  const fieldIds = useMemo(() => currentGroupFields.map((field) => (field.id ?? field.uuid)!), [currentGroupFields]);

  const onAddFieldClick = () => {
    setIsAddingField(true);
  };

  const onCancelAddField = () => {
    setIsAddingField(false);
  };

  const onCommitAddField = useCallback(
    ({ name, type }: InlineAddFieldPlaceholderCommitPayload) => {
      dispatch(
        addFormField({
          groupId: groupId!,
          field: {
            fieldCaption: { translations: createTranslations(name, locales ?? []) },
            fieldType: type.value,
            required: false,
            removed: false,
            lockDown: false,
            importEnabled: false,
            groupId: groupId,
            position: currentGroupFields.length + 1,
            uuid: crypto.randomUUID(),
          } as FormBuilderField,
        })
      );

      setIsAddingField(false);
    },
    [currentGroupFields.length, dispatch, groupId, locales]
  );

  return (
    <FormBuilderAccordion id={groupId} caption={groupCaption} onGroupClick={onGroupClick} className={className}>
      <>
        <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
          {currentGroupFields?.map((field) => (
            <FormBuilderFieldPreview key={field.id ?? field.uuid} field={field} groupId={groupId!}>
              <FormBuilderDynamicField
                key={`${groupId}-${field.id ?? field.uuid}`}
                field={field as FormBuilderField}
                groupId={groupId!}
              />
            </FormBuilderFieldPreview>
          ))}
        </SortableContext>

        {isAddingField ? (
          <InlineAddFieldPlaceholder onCommit={onCommitAddField} onCancel={onCancelAddField} />
        ) : (
          <FormBuilderAddButton key={groupId} onClick={onAddFieldClick} label="Add Field" />
        )}
      </>
    </FormBuilderAccordion>
  );
};

export default React.memo(FormBuilderGroup);
